// The practice dock's clock: a look-ahead scheduler on the AudioContext clock
// that plays the band (drums and bass) and the metronome's click, either or
// both. The clock keeps time and the player follows it; the microphone (or
// the player's taps) only decides which chord the bass plays over, unless the
// chords are set to move on by themselves every so many beats.

import { BassVoice, DrumKit, MetronomeClick } from './synth';
import { DRUM_VOICES, VELOCITY, bassBar, bassMidi } from './patterns';
import { SampleBank, SampledBass, SampledKit } from './sampler';
import { getAudioContext, isAudioSupported } from '../practice-dock/audio';

const LOOKAHEAD = 0.12; // seconds of audio scheduled ahead of the clock
const TICK_MS = 25;
// A chord change heard within this fraction of a beat after a bass note
// started slides that note to the new chord, so a change on the downbeat
// (which the listener hears ~150ms late) doesn't leave a wrong note there.
const GLIDE_WINDOW = 0.5;
// Don't hold up Play for longer than this waiting on samples; the synth fills in.
const SAMPLE_WAIT_MS = 3000;
export const MIN_BPM = 30;
export const MAX_BPM = 240;

export const clampBpm = bpm => Math.max(MIN_BPM, Math.min(MAX_BPM, Math.round(bpm)));

// Dedicated workers aren't throttled in background tabs the way the page's
// own timers are, so the groove keeps going if the player switches tabs.
function createTicker(callback) {
    try {
        const source = 'let id = null; onmessage = e => { clearInterval(id); if (e.data > 0) id = setInterval(() => postMessage(0), e.data); };';
        const url = URL.createObjectURL(new Blob([source], { type: 'text/javascript' }));
        const worker = new Worker(url);
        URL.revokeObjectURL(url);
        worker.onmessage = callback;
        return {
            start: () => worker.postMessage(TICK_MS),
            stop: () => worker.postMessage(0),
            close: () => worker.terminate(),
        };
    } catch (e) {
        let id = null;
        return {
            start: () => { clearInterval(id); id = setInterval(callback, TICK_MS); },
            stop: () => clearInterval(id),
            close: () => clearInterval(id),
        };
    }
}

export default class BackingTrackEngine {
    constructor({ onBeat, onTempo } = {}) {
        this.onBeat = onBeat;
        this.onTempo = onTempo;
        this.bpm = 100;
        this.preset = null;
        this.grid = null;
        this.bassStyle = 'root';
        this.chord = null;
        this.nextChord = null;
        this.volumes = { drums: 0.8, bass: 0.8, click: 0.8 };
        this.sounds = { drums: 'acoustic', bass: 'electric' };
        this.sampled = {};
        this.voices = [];
        this.barNotes = [];
        this.timers = [];
        this.playing = false;
        // Layers: the band (drums and bass) and the metronome's click.
        this.band = false;
        this.bandFrom = 0;
        this.click = false;
        // The metronome's own bar, used when the band isn't setting one.
        // accents: per beat, 2 accented, 1 normal, 0 silent.
        this.meter = { beats: 4, subdivision: 1, accents: [2, 1, 1, 1] };
        this.ramp = null;
        this.auto = null;
        this.closed = false;
    }

    static isSupported() {
        return isAudioSupported();
    }

    ensureContext() {
        const ctx = getAudioContext();
        if (this.ctx !== ctx) {
            const compressor = ctx.createDynamicsCompressor();
            compressor.threshold.value = -14;
            compressor.ratio.value = 4;
            compressor.connect(ctx.destination);
            this.master = ctx.createGain();
            this.master.gain.value = 0.9;
            this.master.connect(compressor);
            this.drumBus = ctx.createGain();
            this.bassBus = ctx.createGain();
            this.clickBus = ctx.createGain();
            this.drumBus.connect(this.master);
            this.bassBus.connect(this.master);
            this.clickBus.connect(this.master);
            this.synthKit = new DrumKit(ctx, this.drumBus);
            this.synthBass = new BassVoice(ctx, this.bassBus);
            this.metronome = new MetronomeClick(ctx, this.clickBus);
            this.bank = new SampleBank(ctx);
            this.sampled = {};
            if (!this.ticker) this.ticker = createTicker(() => this.schedule());
            this.ctx = ctx;
            this.setVolumes(this.volumes);
        }
        return this.ctx;
    }

    // Recorded instruments once loaded, the synth until then (or if they fail).
    get kit() {
        return (this.sounds.drums === 'acoustic' && this.sampled.drums) || this.synthKit;
    }

    get bass() {
        return (this.sounds.bass !== 'synth' && this.sampled[this.sounds.bass]) || this.synthBass;
    }

    // Shape of the next bar: the groove's while the band plays (and for its
    // count-in), otherwise the metronome's. The bar being played keeps its
    // shape (this.shape) until it ends, since its steps were counted in it.
    barShape() {
        const grooved = this.band && this.preset && (this.barIndex < 0 || this.barIndex >= this.bandFrom);
        return grooved ? { ...this.preset, own: false } : { ...this.meter, swing: 0, own: true };
    }

    // drums: 'acoustic' | 'synth'; bass: 'electric' | 'upright' | 'synth'.
    setSounds(sounds) {
        this.sounds = { ...this.sounds, ...sounds };
        if (this.ctx) this.loadSounds();
    }

    loadSounds() {
        this.ensureContext();
        const jobs = [];
        if (this.sounds.drums === 'acoustic' && !this.sampled.drums) {
            jobs.push(this.bank.load('drums').then(kit => {
                this.sampled.drums = new SampledKit(this.ctx, this.drumBus, kit);
            }));
        }
        const bass = this.sounds.bass;
        if (bass !== 'synth' && !this.sampled[bass]) {
            jobs.push(this.bank.load(bass).then(notes => {
                this.sampled[bass] = new SampledBass(this.ctx, this.bassBus, notes);
            }));
        }
        return Promise.all(jobs).catch(error => {
            console.warn('Backing track samples unavailable, using synth sounds', error);
        });
    }

    // Turns the band and/or the click on, starting the clock if it isn't
    // running. A band joining a running click comes in on the next bar.
    async start({ band = this.band, click = this.click, countIn = true } = {}) {
        const ctx = this.ensureContext();
        // Chords moving by themselves wait for a band that's on its way.
        if (band && !this.band) this.bandPending = true;
        await ctx.resume();
        if (band && !this.band) {
            await Promise.race([this.loadSounds(), new Promise(resolve => setTimeout(resolve, SAMPLE_WAIT_MS))]);
        }
        this.bandPending = false;
        if (this.closed) return; // the dock went while the samples loaded
        if (this.playing) {
            if (band && !this.band) {
                this.bandFrom = this.barIndex + 1;
                this.barNotes = [];
                this.autoStarting = true;
            }
            this.band = band;
            this.click = click;
            return;
        }
        this.band = band;
        this.click = click;
        this.step = 0;
        // The count-in is bar -1: four rim clicks before the band comes in.
        this.barIndex = band && countIn ? -1 : 0;
        this.bandFrom = 0;
        this.shape = this.barShape();
        this.autoStarting = true;
        this.rampBars = 0;
        this.barNotes = [];
        this.nextTime = ctx.currentTime + 0.08;
        this.playing = true;
        this.ticker.start();
        this.schedule();
    }

    // Turns layers off; the clock stops when nothing is left playing.
    stopLayers({ band = false, click = false }) {
        if (band) {
            this.band = false;
            this.bandPending = false;
            this.releaseBass();
        }
        if (click) this.click = false;
        if (!this.band && !this.click) this.stop();
    }

    stop() {
        this.playing = false;
        this.band = false;
        this.bandPending = false;
        this.click = false;
        if (this.ticker) this.ticker.stop();
        this.timers.forEach(clearTimeout);
        this.timers = [];
        this.releaseBass();
    }

    releaseBass() {
        if (this.ctx) {
            const now = this.ctx.currentTime;
            this.voices.forEach(voice => voice.instrument.release(voice, now));
        }
        this.voices = [];
    }

    // The AudioContext is the dock's (see practice-dock/audio), so it's left open.
    close() {
        this.closed = true;
        this.stop();
        if (this.ticker) this.ticker.close();
        this.ticker = null;
        if (this.master) this.master.disconnect();
        this.ctx = null;
    }

    setPattern(preset, grid) {
        this.preset = preset;
        this.grid = grid;
        this.barNotes = bassBar(this.bassStyle, preset, Math.max(this.barIndex || 0, 0)).filter(note => note.step >= (this.step || 0));
    }

    setMeter(meter) {
        this.meter = { ...this.meter, ...meter };
    }

    setTempo(bpm) {
        this.bpm = bpm;
    }

    // Speed up while playing: by `step` BPM every `everyBars` bars (or on each
    // bumpTempo() when everyBars is null, e.g. each time round a loop) until
    // `to`. null turns it off.
    setRamp(ramp) {
        this.ramp = ramp;
        this.rampBars = 0;
    }

    bumpTempo(time) {
        const { ramp } = this;
        const to = ramp ? Math.min(ramp.to, MAX_BPM) : 0;
        if (!ramp || !this.playing || this.bpm >= to) return;
        this.bpm = Math.min(to, this.bpm + ramp.step);
        this.at(time === undefined ? this.ctx.currentTime : time, () => this.onTempo && this.onTempo(this.bpm));
    }

    // Chords that move on by themselves: every `beats` beats `next(time)` is
    // asked for { chord, nextChord } to play from `time`, counting from the
    // next bar line (once the band is in). null hands the chords back to
    // setChord().
    setAutoChords(auto) {
        if (auto && this.auto && auto.beats === this.auto.beats) {
            this.auto = auto;
            return;
        }
        this.auto = auto;
        this.autoStarting = true;
    }

    setBassStyle(style) {
        this.bassStyle = style;
        if (this.preset) {
            this.barNotes = bassBar(style, this.preset, Math.max(this.barIndex || 0, 0)).filter(note => note.step >= (this.step || 0));
        }
    }

    setVolumes(volumes) {
        this.volumes = { ...this.volumes, ...volumes };
        if (this.ctx) {
            const now = this.ctx.currentTime;
            this.drumBus.gain.setTargetAtTime(this.volumes.drums, now, 0.02);
            this.bassBus.gain.setTargetAtTime(this.volumes.bass, now, 0.02);
            this.clickBus.gain.setTargetAtTime(this.volumes.click, now, 0.02);
        }
    }

    // chord/nextChord come from parseChord(); null chord rests the bass.
    setChord(chord, nextChord = null) {
        const previous = this.chord;
        this.chord = chord;
        this.nextChord = nextChord;
        if (!this.playing || !this.band || !this.ctx || previous === chord) {
            return;
        }
        const now = this.ctx.currentTime;
        const beat = 60 / this.bpm;
        this.voices = this.voices.filter(voice => voice.end > now);
        if (!chord) {
            // Let the note under way ring out but drop anything scheduled ahead.
            this.voices.filter(voice => voice.start > now).forEach(voice => voice.instrument.release(voice, now));
            return;
        }
        this.voices.slice().forEach(voice => {
            const midi = bassMidi(voice.degree, chord, nextChord);
            if (midi === voice.midi) {
                return;
            }
            if (voice.start >= now - GLIDE_WINDOW * beat) {
                voice.instrument.retune(voice, midi, Math.max(now, voice.start));
            } else if (voice.end - now > beat / 4) {
                // A long note from before the change: cut it and restart on the new chord.
                const end = voice.end;
                voice.instrument.release(voice, now);
                const fresh = this.bass.note(midi, now + 0.01, end - now, voice.velocity * 0.85);
                fresh.degree = voice.degree;
                fresh.velocity = voice.velocity;
                this.voices.push(fresh);
            }
        });
    }

    schedule() {
        if (!this.playing) return;
        while (this.nextTime < this.ctx.currentTime + LOOKAHEAD) {
            if (this.step >= this.shape.beats * this.shape.subdivision) {
                this.step = 0;
                this.barIndex += 1;
                this.shape = this.barShape();
                if (this.barIndex > 0) this.rampBars += 1;
                if (this.ramp && this.ramp.everyBars && this.rampBars > 0 && this.rampBars % this.ramp.everyBars === 0) {
                    this.bumpTempo(this.nextTime);
                }
            }
            this.playStep(this.step, this.barIndex, this.nextTime);
            this.nextTime += 60 / this.bpm / this.shape.subdivision;
            this.step += 1;
        }
        const now = this.ctx.currentTime;
        this.voices = this.voices.filter(voice => voice.end > now);
    }

    playStep(step, bar, stepTime) {
        const { subdivision, swing } = this.shape;
        const beat = 60 / this.bpm;
        let time = stepTime;
        if (swing && subdivision === 4 && step % 4 === 2) {
            time += swing * beat / 6; // full swing puts the off-beat on the last triplet
        }
        const onBeat = step % subdivision === 0;
        if (onBeat) {
            this.notifyBeat(time, bar, step / subdivision);
        }

        if (bar < 0) {
            if (onBeat) this.kit.play('rim', time, step === 0 ? 1 : 0.6);
            return;
        }

        if (this.click) this.playClick(step, time);

        // Chords start moving on a bar line, and not before the band is in.
        if (step === 0 && this.autoStarting && !this.bandPending && (!this.band || bar >= this.bandFrom)) {
            this.autoStarting = false;
            this.beatCount = 0;
        }
        if (onBeat && this.auto && !this.autoStarting) {
            if (this.beatCount % this.auto.beats === 0) {
                const { chord, nextChord } = this.auto.next(time);
                this.chord = chord;
                this.nextChord = nextChord;
            }
            this.beatCount += 1;
        }

        if (!this.band || bar < this.bandFrom || !this.preset) return;

        DRUM_VOICES.forEach(voice => {
            const velocity = VELOCITY[(this.grid[voice] || '')[step]] || 0;
            if (velocity > 0) this.kit.play(voice, time, velocity);
        });

        if (step === 0 || !this.barNotes.length) {
            this.barNotes = bassBar(this.bassStyle, this.preset, bar);
        }
        if (!this.chord) return;
        this.barNotes.filter(note => note.step === step).forEach(note => {
            const midi = bassMidi(note.degree, this.chord, this.nextChord);
            const velocity = step % subdivision === 0 ? 0.9 : 0.75;
            const duration = note.length * beat / subdivision * 0.92;
            const voice = this.bass.note(midi, time, duration, velocity);
            voice.degree = note.degree;
            voice.velocity = velocity;
            this.voices.push(voice);
        });
    }

    playClick(step, time) {
        const { subdivision, own } = this.shape;
        if (step % subdivision === 0) {
            const beat = step / subdivision;
            // In the band's bar the click marks beats only, accenting the one.
            const level = own ? (this.meter.accents[beat] ?? 1) : (beat === 0 ? 2 : 1);
            if (level > 0) this.metronome.play(time, level === 2 ? 'accent' : 'beat');
        } else if (own) {
            this.metronome.play(time, 'sub');
        }
    }

    // Runs `callback` when `time` (on the audio clock) is heard.
    at(time, callback) {
        const delay = Math.max(0, (time - this.ctx.currentTime) * 1000);
        const timer = setTimeout(() => {
            this.timers = this.timers.filter(t => t !== timer);
            callback();
        }, delay);
        this.timers.push(timer);
    }

    notifyBeat(time, bar, beat) {
        if (!this.onBeat) return;
        this.at(time, () => {
            if (this.playing) this.onBeat({ bar, beat, time });
        });
    }
}
